import { useEffect, useRef, useState } from 'react'
import { InputManager, isTouchDevice } from '@/arcade'
import type { Direction } from '@/types/game'

interface TouchControlsProps {
  onSlide: (dir: Direction) => void
}

function vecToDir(x: number, y: number): Direction | null {
  if (Math.abs(x) < 0.4 && Math.abs(y) < 0.4) return null
  if (Math.abs(x) > Math.abs(y)) return x > 0 ? 'right' : 'left'
  return y > 0 ? 'down' : 'up'
}

export function TouchControls({ onSlide }: TouchControlsProps) {
  const swipeRef = useRef<HTMLDivElement>(null)
  const joyRef = useRef<HTMLDivElement>(null)
  const slideRef = useRef(onSlide)
  slideRef.current = onSlide
  const [touch] = useState(() => isTouchDevice())

  useEffect(() => {
    const input = new InputManager()
    input.attach(window)

    const joy = joyRef.current
    if (touch && joy) {
      input.mountJoystick(joy, { mode: 'static', color: 'rgba(255,180,84,0.7)', size: 96 })
    }

    let raf = 0
    let last: Direction | null = null
    const loop = () => {
      input.pollGamepad()
      const dir = vecToDir(input.state.moveX, input.state.moveY)
      if (dir && dir !== last) {
        last = dir
        slideRef.current(dir)
      } else if (!dir) {
        last = null
      }
      raf = requestAnimationFrame(loop)
    }
    raf = requestAnimationFrame(loop)

    const zone = swipeRef.current
    let startX = 0
    let startY = 0
    let tracking = false
    const onStart = (e: PointerEvent) => {
      startX = e.clientX
      startY = e.clientY
      tracking = true
    }
    const onEnd = (e: PointerEvent) => {
      if (!tracking) return
      tracking = false
      const dx = e.clientX - startX
      const dy = e.clientY - startY
      if (Math.abs(dx) < 24 && Math.abs(dy) < 24) return
      const dir = vecToDir(dx, dy)
      if (dir) slideRef.current(dir)
    }
    zone?.addEventListener('pointerdown', onStart)
    zone?.addEventListener('pointerup', onEnd)
    zone?.addEventListener('pointercancel', () => (tracking = false))

    return () => {
      cancelAnimationFrame(raf)
      input.detach(window)
      zone?.removeEventListener('pointerdown', onStart)
      zone?.removeEventListener('pointerup', onEnd)
    }
  }, [touch])

  return (
    <>
      {/* Full-board swipe layer — sits BELOW HUD/dock so buttons always tap. */}
      <div
        ref={swipeRef}
        data-swipe-zone="true"
        className="absolute inset-0"
        style={{ touchAction: 'none', zIndex: 1 }}
        aria-hidden="true"
      />
      {/* Visible joystick pad, anchored bottom-left, kept clear of the Pause
          button (top-right) and the powerup dock (below the board). */}
      {touch && (
        <div
          ref={joyRef}
          data-joystick-zone="true"
          className="absolute"
          style={{
            left: 'calc(1rem + var(--safe-left, 0px))',
            bottom: 'calc(1rem + var(--safe-bottom, 0px))',
            width: 132,
            height: 132,
            touchAction: 'none',
            zIndex: 2,
          }}
        >
          <div
            className="pointer-events-none absolute inset-0 rounded-full border border-[var(--edge)]/50"
            style={{ background: 'radial-gradient(circle at 50% 40%, rgba(255,180,84,0.12), rgba(0,0,0,0.18))', boxShadow: 'var(--shadow-emboss)' }}
          />
        </div>
      )}
    </>
  )
}
